import { ABI, SamplePack } from '@arpeggi/shared'
import { Box } from '@arpeggi/ui'
import format from 'date-fns/format'
import secondsToMilliseconds from 'date-fns/secondsToMilliseconds'
import { ethers } from 'ethers'
import { FC, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Skeleton from '../../__legacy__/components/core/Skeleton'
import SearchBar from '../../__legacy__/components/platform/new-explore/components/SearchBar'
import { useSamplePacks } from '../../__legacy__/queries/samplePacks'
import { useUGCContractMetadata } from '../web3-utils'
import { Card, Page } from './primitives'

const PackCardSkeleton: FC = () => (
  <Card.Root css={{ flexBasis: '216', flexGrow: 0, flexShrink: 0, height: '296px' }}>
    <Card.ThumbnailWrapper>
      <Skeleton css={{ position: 'absolute', width: '100%', height: '100%' }} />
    </Card.ThumbnailWrapper>
    <Card.Content>
      <Skeleton css={{ height: '16px', marginBottom: '4px', width: '100px' }} />
      <Skeleton css={{ height: '20px', marginBottom: '4px', width: '160px' }} />
    </Card.Content>
  </Card.Root>
)

const usePackInfo = (pack: SamplePack) => {
  const { edition } = pack
  const { contractAddress, chain, price, totalVolume } = edition
  const { totalMinted } = useUGCContractMetadata({
    contractAddress,
    abi: ABI.legacy.ERC721Generic_11_21_22,
    chain
  })
  const tag = useMemo(() => {
    const { publicSaleStartTime, publicSaleEndTime } = edition
    const parsedStartTime = new Date(publicSaleStartTime).valueOf()
    const parsedEndTime = new Date(publicSaleEndTime).valueOf()
    const now = Date.now()
    if (parsedStartTime < now && parsedEndTime > now) {
      return { msg: 'LIVE', variant: 'success' }
    }
    if (parsedEndTime < now) {
      return { msg: 'SALE ENDED', variant: 'disabled' }
    }
    return { msg: format(parsedStartTime, 'MMM. do h:mm a'), variant: 'default' }
  }, [pack])
  return { tag, totalMinted, price, totalVolume }
}

const Availiability: FC<{ totalMinted: number; volume: number }> = ({ totalMinted, volume }) => {
  if (!volume) return <Card.Text variant="caption">Unlimited</Card.Text>
  return (
    <Card.Text variant="caption" color="secondary">
      Available:{' '}
      <Card.Text as="span" variant="caption" color="primary">
        {`${totalMinted}/${volume}`}
      </Card.Text>
    </Card.Text>
  )
}

const Price: FC<{ price: string | number }> = ({ price }) => {
  if (price === 0 || price === '0') return <Card.Text variant="caption">Free</Card.Text>
  return (
    <Card.Text variant="caption" color="secondary">
      Price:{' '}
      <Card.Text as="span" variant="caption" color="primary">
        {`${ethers.utils.formatUnits(price, 'ether')} ETH`}
      </Card.Text>
    </Card.Text>
  )
}

const CollectiblePackCard: FC<{ pack: SamplePack }> = ({ pack }) => {
  const { title, user, coverImage, edition, id } = pack
  const { tag, totalMinted, price, totalVolume } = usePackInfo(pack)
  return (
    <Card.Root
      as={Link}
      to={`/sample-packs/${id}`}
      clickable
      css={{ flexBasis: '216', flexGrow: 0, flexShrink: 0 }}
    >
      <Card.ThumbnailWrapper>
        <Card.Thumbnail src={coverImage?.url} />
        <Card.Tag variant={tag.variant as any}>{tag.msg}</Card.Tag>
      </Card.ThumbnailWrapper>
      <Card.Content>
        <Card.Text variant="sub" color="secondary">
          {user.displayName}
        </Card.Text>
        <Card.Text variant="title" color="primary">
          {title}
        </Card.Text>
        <Box row justifyBetween>
          <Availiability totalMinted={totalMinted} volume={totalVolume} />
          <Price price={price} />
        </Box>
      </Card.Content>
    </Card.Root>
  )
}

const NonCollectiblePackCard: FC<{ pack: SamplePack }> = ({ pack }) => {
  const { title, user, coverImage, id } = pack
  return (
    <Card.Root
      as={Link}
      to={`/sample-packs/${id}`}
      clickable
      css={{ flexBasis: '216', flexGrow: 0, flexShrink: 0 }}
    >
      <Card.ThumbnailWrapper>
        <Card.Thumbnail src={coverImage?.url} />
      </Card.ThumbnailWrapper>
      <Card.Content>
        <Card.Text variant="sub" color="secondary">
          {user.displayName}
        </Card.Text>
        <Card.Text variant="title" color="primary">
          {title}
        </Card.Text>
      </Card.Content>
    </Card.Root>
  )
}

const PackCard: FC<{ pack: SamplePack }> = ({ pack }) => {
  const { edition } = pack
  if (!edition) return <NonCollectiblePackCard pack={pack} />
  return <CollectiblePackCard pack={pack} />
}

export const Packs: FC = () => {
  const [search, setSearch] = useState<string>()
  const { data, fetchNextPage, hasNextPage, isLoading } = useSamplePacks({
    params: {
      perPage: 50,
      order: 'desc',
      orderBy: 'createdAt',
      search
    },
    options: {
      refetchInterval: secondsToMilliseconds(30)
    }
  })
  return (
    <Page.Root>
      <Page.Flare type="one" />
      <Page.Flare type="two" />
      <Page.Header>
        <Page.Title>Browse sample packs</Page.Title>
        <Page.Subtitle>Discover our endless library of user generated sounds</Page.Subtitle>
        <Page.SearchContainer>
          <SearchBar
            css={{ flex: 1 }}
            value={search}
            onChange={(evt) => setSearch(evt.target.value)}
          />
        </Page.SearchContainer>
      </Page.Header>
      <Page.Divider />
      <Page.Content>
        <Box row wrap justifyCenter css={{ gap: '16px' }}>
          {(!data || isLoading) && (
            <>
              <PackCardSkeleton />
              <PackCardSkeleton />
              <PackCardSkeleton />
              <PackCardSkeleton />
              <PackCardSkeleton />
              <PackCardSkeleton />
              <PackCardSkeleton />
              <PackCardSkeleton />
              <PackCardSkeleton />
              <PackCardSkeleton />
            </>
          )}
          {data && data.pages[0].data.map((pack) => <PackCard pack={pack} key={pack.id} />)}
        </Box>
      </Page.Content>
    </Page.Root>
  )
}
